import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import {
  TrendingUp, Wallet, BedDouble, LogIn, LogOut, AlertCircle, Sparkles,
  Users, FileText, DollarSign, Activity, ArrowUpRight, Plus, PlayCircle,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Metric, Badge, StatusDot, SectionTitle, Section, EmptyState, Skeleton } from "@/components/primitives";

// ---------- Palette used for charts (approved system only) ----------
const CHART = {
  primary: "#37F4B5",     // mint — primary bars/areas
  secondary: "#014751",   // deep teal — secondary bars
  orange:  "#014751",
  gold:    "#37F4B5",
  steel:   "#014751",
  soft:    "#14C48E",
  alice:   "#E6EFF0",     // grid
  success: "#14C48E",
  warn:    "#F59E0B",
  error:   "#DC2626",
  neutral: "#5A6E73",     // axis text
  // legacy aliases
  honeydew: "#37F4B5",
  honeydewDark: "#014751",
};

// ---------- Helpers ----------
const inr = (m) => m == null ? "—" : `₹${(Number(m) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const num = (n) => (n ?? 0).toLocaleString("en-IN");
const pct = (n) => `${(n ?? 0).toFixed(1)}%`;
const fmtDate = (d) => (d || "").slice(0, 16).replace("T", " ");

const STATUS_TONE = {
  open: "info", closing: "warning", closed: "success", reopening: "warning",
  reopened: "info", failed: "error",
  initiated: "info", preflight_blocked: "error", preflight_passed: "success",
  settling: "warning", settled: "success", invoicing: "warning",
  awaiting_key_return: "warning", completed: "success", cancelled: "neutral",
  waiting_external: "warning",
};

export default function Dashboard() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    checkoutSummary: null,
    stats: null,
    events: [],
    payments: [],
    folios: [],
    reservations: [],
    stays: [],
    businessDays: [],
    audit: [],
  });

  useEffect(() => {
    (async () => {
      const safe = (p) => p.then(r => r.data).catch(() => null);
      const [checkoutSummary, stats, events, payments, folios, reservations, stays, businessDays, audit, movement] = await Promise.all([
        safe(api.get("/checkout/reports/summary")),
        safe(api.get("/stats")),
        safe(api.get("/events?limit=20")),
        safe(api.get("/payments?page_size=20")),
        safe(api.get("/folios?page_size=20")),
        safe(api.get("/pms/reservations/?page_size=10")),
        safe(api.get("/pms/stays/?page_size=10")),
        safe(api.get("/ledger/business-days?page_size=8")),
        safe(api.get("/audit-logs?limit=20")),
        safe(api.get("/pms/stays/movement?days=7")),
      ]);
      setData({
        checkoutSummary: checkoutSummary || {},
        stats: stats || {},
        events: (events?.items || events || []).slice(0, 20),
        payments: (payments?.items || payments || []).slice(0, 20),
        folios: (folios?.items || folios || []).slice(0, 20),
        reservations: (reservations?.items || reservations || []).slice(0, 10),
        stays: (stays?.items || stays || []).slice(0, 10),
        businessDays: (businessDays?.items || businessDays || []).slice(0, 8),
        audit: (audit?.items || audit || []).slice(0, 20),
        movement: movement || null,
      });
      setLoading(false);
    })();
    // keep today's movement live without reloading the whole dashboard
    const t = setInterval(() => api.get("/pms/stays/movement?days=7").then(r => setData(d => ({ ...d, movement: r.data }))).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, []);

  // ---------- Derived metrics ----------
  const kpis = useMemo(() => {
    const co = data.checkoutSummary || {};
    const activePayments = (data.payments || []).filter(p => p.status === "completed");
    const totalReceivedToday = activePayments.reduce((s, p) => s + (p.amount_minor || 0), 0);
    const outstandingFolios = (data.folios || []).filter(f => (f.balance_minor_cache || 0) > 0);
    const totalOutstanding = outstandingFolios.reduce((s, f) => s + (f.balance_minor_cache || 0), 0);
    const checkins = data.stats?.in_house_stays ?? (data.stays || []).filter(s => s.status === "checked_in").length;
    const checkouts = (co.by_status?.completed ?? 0);
    const inProgress = (co.by_status?.settling ?? 0) + (co.by_status?.invoicing ?? 0) + (co.by_status?.awaiting_key_return ?? 0);
    const totalRooms = data.stats?.rooms ?? 0;
    // Server-side, hotel-scoped count (the stays list above is only the first page)
    const occupied = data.stats?.occupied_rooms ?? (data.stays?.filter?.(s => s.status === "checked_in").length ?? 0);
    const occupancy = totalRooms ? (occupied / totalRooms) * 100 : 0;
    return {
      revenueToday: totalReceivedToday,
      outstanding: totalOutstanding,
      outstandingCount: outstandingFolios.length,
      checkins, checkouts, inProgress,
      occupancy,
      totalRooms,
      occupied,
      avgCheckoutDuration: co.avg_checkout_duration_seconds,
      blockedCheckouts: co.by_status?.preflight_blocked ?? 0,
    };
  }, [data]);

  // Revenue trend from last N business day snapshots (fallback: last N payments grouped by day)
  const revenueTrend = useMemo(() => {
    // Use business_day_closes with debit/credit totals as a lightweight proxy
    const bdays = (data.businessDays || []).slice().sort((a, b) => (a.business_day || "").localeCompare(b.business_day || ""));
    if (bdays.length > 0) {
      return bdays.map(b => ({
        day: (b.business_day || "").slice(5),
        revenue: (b.credit_total_minor || 0) / 100,
        payments: (b.debit_total_minor || 0) / 100,
      }));
    }
    // Fallback: group payments by day
    const byDay = {};
    (data.payments || []).forEach(p => {
      const day = (p.created_at || "").slice(5, 10);
      if (!day) return;
      byDay[day] = (byDay[day] || 0) + (p.amount_minor || 0);
    });
    return Object.entries(byDay).slice(-7).map(([day, v]) => ({ day, revenue: v / 100, payments: v / 100 }));
  }, [data]);

  // Room status donut
  const roomStatusData = useMemo(() => {
    const total = data.stats?.rooms || 0;
    const occ = kpis.occupied;
    const vacant = Math.max(total - occ, 0);
    return [
      { name: "Occupied", value: occ, color: CHART.primary },
      { name: "Vacant",   value: vacant, color: CHART.honeydew },
    ];
  }, [data.stats, kpis]);

  // Payment methods bar
  const paymentMethods = useMemo(() => {
    const bag = {};
    (data.payments || []).forEach(p => {
      const m = p.method || "other";
      bag[m] = (bag[m] || 0) + (p.amount_minor || 0);
    });
    return Object.entries(bag).map(([method, total]) => ({
      method: method.toUpperCase(), total: total / 100,
    }));
  }, [data.payments]);

  // Check-ins vs Check-outs · real actual_arrival / actual_departure counts per hotel-local day (GET /pms/stays/movement)
  const cicoData = useMemo(() => {
    const series = data.movement?.series || [];
    const today = data.movement?.today;
    return series.map(d => ({
      day: d.date === today ? "Today" : d.date.slice(5),
      "Check-ins": d.check_ins || 0, "Check-outs": d.check_outs || 0,
      expected_arrivals: d.expected_arrivals || 0, expected_departures: d.expected_departures || 0,
    }));
  }, [data.movement]);

  // Alerts / exceptions
  const alerts = useMemo(() => {
    const items = [];
    const outstanding = (data.folios || []).filter(f => (f.balance_minor_cache || 0) > 0);
    if (outstanding.length > 0) items.push({
      tone: "warning", icon: Wallet,
      title: `${outstanding.length} folios with outstanding balance`,
      hint: `${inr(outstanding.reduce((s,f) => s + f.balance_minor_cache, 0))} total AR`,
      onClick: () => nav("/finance/folios"),
    });
    const failedDays = (data.businessDays || []).filter(b => b.status === "failed");
    if (failedDays.length > 0) items.push({
      tone: "error", icon: AlertCircle,
      title: `${failedDays.length} failed night audit(s)`,
      hint: "Review integrity check results",
      onClick: () => nav("/ledger/business-days"),
    });
    const blocked = kpis.blockedCheckouts;
    if (blocked > 0) items.push({
      tone: "error", icon: LogOut,
      title: `${blocked} checkout(s) blocked at preflight`,
      hint: "Awaiting override or resolution",
      onClick: () => nav("/checkout/queue"),
    });
    const locked = (data.businessDays || []).filter(b => ["closed","closing","reopening"].includes(b.status));
    if (locked.length > 0) items.push({
      tone: "info", icon: Sparkles,
      title: `${locked.length} fiscal-locked business day(s)`,
      hint: "Late postings require workflow approval",
      onClick: () => nav("/ledger/business-days"),
    });
    return items;
  }, [data.folios, data.businessDays, kpis, nav]);

  const quickActions = [
    { label: "New Reservation", icon: Plus, to: "/pms/reservations" },
    { label: "Check-in",        icon: LogIn, to: "/pms/arrivals" },
    { label: "Record Payment",  icon: DollarSign, to: "/finance/payments" },
    { label: "Checkout Guest",  icon: LogOut, to: "/checkout/queue" },
    { label: "Open Folio",      icon: FileText, to: "/finance/folios" },
    { label: "Run Night Audit", icon: PlayCircle, to: "/ledger/business-days" },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Command Center"
        subtitle="Executive Overview"
        testid="dashboard-header"
        action={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-50 border border-emerald-100">
              <StatusDot tone="success" />
              <span className="text-[12px] font-semibold text-emerald-700">All systems healthy</span>
            </div>
          </div>
        }
      />

      {/* ============ HERO KPIs ============ */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-kpi-grid">
          <Metric label="Occupancy" value={pct(kpis.occupancy)} hint={`${kpis.occupied} of ${kpis.totalRooms} rooms`} icon={BedDouble} tone="accent" testid="kpi-occupancy" />
          <Metric label="Revenue Today" value={inr(kpis.revenueToday)} hint={`from ${(data.payments||[]).length} payments`} icon={TrendingUp} tone="highlight" testid="kpi-revenue" />
          <Metric label="Outstanding" value={inr(kpis.outstanding)} hint={`${kpis.outstandingCount} folios`} icon={Wallet} testid="kpi-outstanding" />
          <Metric label="Rooms Available" value={num(Math.max((kpis.totalRooms - kpis.occupied), 0))} hint={`${kpis.totalRooms} total`} icon={BedDouble} testid="kpi-rooms" />
          <Metric label="Check-ins" value={num(kpis.checkins)} hint="in-house" icon={LogIn} testid="kpi-checkins" />
          <Metric label="Check-outs" value={num(kpis.checkouts)} hint="completed" icon={LogOut} testid="kpi-checkouts" />
          <Metric label="Active Checkouts" value={num(kpis.inProgress)} hint="in workspace" icon={Activity} testid="kpi-active-co" />
          <Metric label="Avg Checkout" value={kpis.avgCheckoutDuration ? `${Math.round(kpis.avgCheckoutDuration/60)}m` : "—"} hint="session duration" icon={Sparkles} testid="kpi-avg-co" />
        </div>
      </Section>

      {/* ============ CHARTS ============ */}
      <Section>
        <SectionTitle hint="Live operational insights from the ledger, stays, and payments engines">Operational Overview</SectionTitle>
        <div className="grid grid-cols-12 gap-4">
          {/* Revenue trend */}
          <Card className="col-span-12 md:col-span-7 p-4 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary">Revenue Trend</div>
                <div className="mt-1 font-display text-[20px] font-semibold text-hocp-textPrimary">Business day activity</div>
              </div>
            </div>
            <div style={{ height: 260 }}>
              {revenueTrend.length === 0 ? (
                <SkeletonBlock h={240}/>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueTrend} margin={{ top: 5, right: 8, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART.honeydewDark} stopOpacity={0.35}/>
                        <stop offset="100%" stopColor={CHART.honeydewDark} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6EFF0" vertical={false}/>
                    <XAxis dataKey="day" stroke={CHART.neutral} fontSize={11} tickLine={false} axisLine={false}/>
                    <YAxis stroke={CHART.neutral} fontSize={11} tickLine={false} axisLine={false} width={60}/>
                    <Tooltip contentStyle={{ background:"#fff", border:"1px solid #E4E6EB", borderRadius:12, fontSize:12 }}/>
                    <Area type="monotone" dataKey="revenue" stroke={CHART.honeydewDark} strokeWidth={2} fill="url(#revGrad)"/>
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          {/* Room status donut */}
          <Card className="col-span-12 md:col-span-5 p-4 md:p-6">
            <div className="mb-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary">Room Status</div>
              <div className="mt-1 font-display text-[20px] font-semibold text-hocp-textPrimary">{pct(kpis.occupancy)} occupied</div>
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={roomStatusData} innerRadius={55} outerRadius={90} paddingAngle={2} dataKey="value" cornerRadius={6}>
                    {roomStatusData.map((e, i) => <Cell key={i} fill={e.color}/>)}
                  </Pie>
                  <Tooltip contentStyle={{ background:"#fff", border:"1px solid #E4E6EB", borderRadius:12, fontSize:12 }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-3">
              {roomStatusData.map(r => (
                <div key={r.name} className="flex items-center gap-2 text-[12px]">
                  <span className="w-2 h-2 rounded-full" style={{ background: r.color }}/>
                  <span className="text-hocp-textSecondary">{r.name}</span>
                  <span className="font-semibold text-hocp-textPrimary tabular-nums">{r.value}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Check-ins vs Check-outs */}
          <Card className="col-span-12 md:col-span-7 p-4 md:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary">Check-ins vs Check-outs</div>
                <div className="mt-1 font-display text-[20px] font-semibold text-hocp-textPrimary">Guest movement</div>
              </div>
              {data.movement && (
                <div className="text-right text-[11.5px] text-hocp-textMuted" data-testid="movement-today">
                  <div>Last {data.movement.days} days · {data.movement.timezone}</div>
                  <div className="mono text-hocp-textPrimary">Today: <span className="font-semibold" data-testid="movement-today-checkins">{data.movement.today_check_ins}</span> in · <span className="font-semibold" data-testid="movement-today-checkouts">{data.movement.today_check_outs}</span> out</div>
                </div>
              )}
            </div>
            <div style={{ height: 240 }} data-testid="movement-chart">
              {cicoData.length === 0 ? (loading ? <SkeletonBlock h={220}/> : <div className="h-full flex items-center justify-center text-[12.5px] text-hocp-textMuted" data-testid="movement-empty">No check-in or check-out activity in this period</div>) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cicoData} margin={{ top: 5, right: 8, left: -8, bottom: 0 }} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6EFF0" vertical={false}/>
                    <XAxis dataKey="day" stroke={CHART.neutral} fontSize={11} tickLine={false} axisLine={false}/>
                    <YAxis stroke={CHART.neutral} fontSize={11} tickLine={false} axisLine={false} width={30} allowDecimals={false}/>
                    <Tooltip contentStyle={{ background:"#fff", border:"1px solid #E4E6EB", borderRadius:12, fontSize:12 }}
                      formatter={(v, name, p) => [v, name]}
                      labelFormatter={(label, payload) => { const d = payload?.[0]?.payload; return d ? `${label} · expected ${d.expected_arrivals} arr / ${d.expected_departures} dep` : label; }}/>
                    <Legend wrapperStyle={{ fontSize:12 }}/>
                    <Bar dataKey="Check-ins" fill={CHART.primary} radius={[6,6,0,0]}/>
                    <Bar dataKey="Check-outs" fill={CHART.honeydewDark} radius={[6,6,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          {/* Payment methods */}
          <Card className="col-span-12 md:col-span-5 p-4 md:p-6">
            <div className="mb-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary">Payment Methods</div>
              <div className="mt-1 font-display text-[20px] font-semibold text-hocp-textPrimary">Distribution</div>
            </div>
            <div style={{ height: 240 }}>
              {paymentMethods.length === 0 ? <SkeletonBlock h={220}/> : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={paymentMethods} layout="vertical" margin={{ top: 5, right: 8, left: 12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6EFF0" horizontal={false}/>
                    <XAxis type="number" stroke={CHART.neutral} fontSize={11} tickLine={false} axisLine={false}/>
                    <YAxis dataKey="method" type="category" stroke={CHART.neutral} fontSize={11} tickLine={false} axisLine={false} width={90}/>
                    <Tooltip contentStyle={{ background:"#fff", border:"1px solid #E4E6EB", borderRadius:12, fontSize:12 }}/>
                    <Bar dataKey="total" fill={CHART.primary} radius={[0,6,6,0]} barSize={16}/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>
      </Section>

      {/* ============ ALERTS + QUICK ACTIONS ============ */}
      <Section>
        <div className="grid grid-cols-12 gap-4">
          {/* Alerts */}
          <Card className="col-span-12 md:col-span-7 p-4 md:p-6">
            <SectionTitle hint="Actionable exceptions across the platform">Alerts & Exceptions</SectionTitle>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-hocp-textSecondary text-[14px]">
                <StatusDot tone="success" /> <span className="ml-2">No open exceptions</span>
              </div>
            ) : (
              <ul className="space-y-3">
                {alerts.map((a, i) => (
                  <li
                    key={i}
                    onClick={a.onClick}
                    className="flex items-start gap-3 p-4 rounded-xl border border-hocp-border hover:border-hocp-borderStrong hover:shadow-hocp-sm transition-all cursor-pointer bg-hocp-alice/25"
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      a.tone === "error" ? "bg-red-50" : a.tone === "warning" ? "bg-amber-50" : "bg-blue-50"
                    }`}>
                      <a.icon className={`w-4 h-4 ${
                        a.tone === "error" ? "text-red-600" : a.tone === "warning" ? "text-amber-600" : "text-blue-600"
                      }`} strokeWidth={2}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-hocp-textPrimary">{a.title}</div>
                      <div className="text-[12.5px] text-hocp-textSecondary mt-0.5">{a.hint}</div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-hocp-textTertiary" strokeWidth={2}/>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Quick Actions */}
          <Card className="col-span-12 md:col-span-5 p-4 md:p-6">
            <SectionTitle hint="One-click operational shortcuts">Quick Actions</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map(qa => (
                <button
                  key={qa.label}
                  onClick={() => nav(qa.to)}
                  className="group flex items-center gap-3 p-4 rounded-xl border border-hocp-border hover:border-hocp-eerie hover:shadow-hocp-md transition-all bg-white text-left"
                  data-testid={`qa-${qa.label.replace(/\s+/g,'-').toLowerCase()}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-hocp-gold text-hocp-eerie flex items-center justify-center group-hover:bg-hocp-eerie group-hover:text-hocp-gold transition-colors shrink-0">
                    <qa.icon className="w-5 h-5" strokeWidth={2.25}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-hocp-textPrimary truncate">{qa.label}</div>
                  </div>
                  <ArrowUpRight className="w-3.5 h-3.5 text-hocp-textTertiary group-hover:text-hocp-eerie" strokeWidth={2}/>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </Section>

      {/* ============ RECENT OPERATIONS ============ */}
      <Section>
        <SectionTitle hint="Real-time streams from the audit and event engines">Recent Activity</SectionTitle>
        <div className="grid grid-cols-12 gap-4">
          {/* Recent Payments */}
          <Card className="col-span-12 md:col-span-6 p-4 md:p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-[16px] font-semibold text-hocp-textPrimary">Recent Payments</div>
              <button onClick={() => nav("/finance/payments")} className="text-[12px] font-semibold text-hocp-textSecondary hover:text-hocp-textPrimary">View all</button>
            </div>
            {(data.payments || []).length === 0 ? (
              <div className="text-[13px] text-hocp-textTertiary py-4">No recent payments.</div>
            ) : (
              <ul className="divide-y divide-hocp-border">
                {data.payments.slice(0, 5).map(p => (
                  <li key={p.id} className="py-2.5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-hocp-honeydew/70 flex items-center justify-center">
                      <DollarSign className="w-3.5 h-3.5 text-hocp-textPrimary" strokeWidth={2}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-hocp-textPrimary mono truncate">{p.payment_no || p.id?.slice(-8)}</div>
                      <div className="text-[11px] text-hocp-textTertiary uppercase tracking-wider">{p.method}</div>
                    </div>
                    <Badge tone={p.status === "completed" ? "success" : p.status === "pending" ? "warning" : "neutral"}>{p.status}</Badge>
                    <div className="text-[14px] font-semibold tabular-nums text-hocp-textPrimary min-w-[80px] text-right">{inr(p.amount_minor)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Recent Business Day Closes */}
          <Card className="col-span-12 md:col-span-6 p-4 md:p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-[16px] font-semibold text-hocp-textPrimary">Business Day Closes</div>
              <button onClick={() => nav("/finance/folios")} className="text-[12px] font-semibold text-hocp-textSecondary hover:text-hocp-textPrimary">View all</button>
            </div>
            {(data.businessDays || []).length === 0 ? (
              <div className="text-[13px] text-hocp-textTertiary py-4">No closes yet — night audit hasn't run.</div>
            ) : (
              <ul className="divide-y divide-hocp-border">
                {data.businessDays.slice(0, 5).map(b => (
                  <li key={b.id} className="py-2.5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-hocp-alice/70 flex items-center justify-center">
                      <FileText className="w-3.5 h-3.5 text-hocp-textPrimary" strokeWidth={2}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-hocp-textPrimary mono">{b.business_day}</div>
                      <div className="text-[11px] text-hocp-textTertiary">{num(b.entry_count)} entries · {inr(b.debit_total_minor)}</div>
                    </div>
                    <Badge tone={STATUS_TONE[b.status] || "neutral"}>{b.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Activity Timeline */}
          <Card className="col-span-12 p-4 md:p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-[16px] font-semibold text-hocp-textPrimary">Activity Timeline</div>
              <button onClick={() => nav("/events")} className="text-[12px] font-semibold text-hocp-textSecondary hover:text-hocp-textPrimary">View events stream</button>
            </div>
            {(data.events || []).length === 0 ? (
              <EmptyState icon={Activity} title="No events yet" subtitle="Events will appear as operations flow through the platform." />
            ) : (
              <ol className="relative border-l-2 border-hocp-border ml-3 space-y-4">
                {data.events.slice(0, 12).map((e, idx) => (
                  <li key={e.id || idx} className="ml-5">
                    <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-hocp-honeydew ring-4 ring-white"/>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-semibold text-hocp-textPrimary">{e.name || e.event_name}</span>
                      <Badge size="sm" tone="neutral">{e.entity_type || "system"}</Badge>
                      <span className="text-[11px] mono text-hocp-textTertiary ml-auto">{fmtDate(e.created_at || e.published_at || e.timestamp)}</span>
                    </div>
                    {e.payload && (
                      <div className="text-[12px] text-hocp-textSecondary mt-1 truncate max-w-3xl">
                        {typeof e.payload === "string" ? e.payload : JSON.stringify(e.payload).slice(0, 160)}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </Section>

      {loading && <div className="fixed bottom-6 right-6 text-[12px] mono text-hocp-textTertiary bg-white border border-hocp-border rounded-full px-4 py-2 shadow-hocp-md">Loading real-time data…</div>}
    </div>
  );
}

function SkeletonBlock({ h = 200 }) {
  return <div className="skeleton rounded-xl" style={{ height: h }}/>;
}
